import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import SearchPinIcon from '../../../public/static/images/new_ad/icons/icon-select-location.svg';
import MetroIcon from '../../../public/static/images/new_ad/icons/icon-metro.svg';
import { getGoogleApiKey } from '../../../utils/helpers/common';
import { Chips } from '../../ui/Chips';
import { ErrorField } from '../../ui/Input/ErrorField';
import { FORM_VALUES } from '../../../utils/helpers/constants';
import Icon from '../../Icon';
import { useLandmarks } from '../../../hooks/useLandmarks';
import { getMapOptions } from '../../../utils/helpers/getMapOptions';

const SelectLocationMapComponent = ({
  initialLocation = { lat: 40.409264, lng: 49.867092 }
}) => {
  const { t } = useTranslation();
  const { change } = useForm();
  const { values } = useFormState();
  const mapRef = useRef();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: getGoogleApiKey()
  });
  const [pinSelected, setPinSelected] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [coordinates, setCoordinates] = useState(initialLocation);
  const { mapOptions, mapStyle } = getMapOptions(isFullScreen);
  const { handleLocationDelete, getLandmarks, locations } =
    useLandmarks(coordinates);

  const onMapClick = event => {
    const newPos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    setCoordinates(newPos);
  };

  useEffect(() => {
    if (!coordinates) return;
    if (mapRef.current && coordinates) {
      mapRef.current.panTo(coordinates);
    }
  }, [coordinates, mapRef.current]);

  useEffect(() => {
    change(
      FORM_VALUES.locationIds,
      locations?.map(location => location.id)
    );
  }, [locations]);

  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  const saveCoordinates = () => {
    setPinSelected(true);
    change(FORM_VALUES.coordinates, coordinates);
    getLandmarks();
    toggleFullScreen();
  };

  const handleBack = () => {
    if (values[FORM_VALUES.coordinates]) {
      setCoordinates(values[FORM_VALUES.coordinates]);
    }
    toggleFullScreen();
  };

  const handleContainerClick =
    isFullScreen || !pinSelected ? null : toggleFullScreen;

  return (
    <>
      <div
        tabIndex={0}
        role="button"
        onClick={handleContainerClick}
        onKeyPress={handleContainerClick}
        className={cn('new-map-container', { 'is-full-screen': isFullScreen })}
      >
        {isFullScreen && (
          <>
            <button
              type="button"
              onClick={handleBack}
              className="new-map-container__close"
            />
            <button
              type="button"
              onClick={saveCoordinates}
              className="new-map-container__save"
            >
              {t('new_ad.save')}
            </button>
          </>
        )}
        {isLoaded && (
          <GoogleMap
            zoom={14}
            options={mapOptions}
            center={initialLocation}
            mapContainerStyle={mapStyle}
            onClick={isFullScreen ? onMapClick : null}
            onLoad={map => {
              mapRef.current = map;
            }}
          >
            {(pinSelected || isFullScreen) && coordinates && (
              <Marker
                draggable
                position={coordinates}
                onDragEnd={onMapClick}
                icon="../static/images/new_ad/icons/icon-map-pin.png"
              />
            )}
          </GoogleMap>
        )}
        {isLoaded && !pinSelected && !isFullScreen && (
          <div className="new-map-container__overlay">
            <button type="button" onClick={toggleFullScreen}>
              <Icon width={20} height={20} IconComponent={SearchPinIcon} />
              {t('new_ad.select_on_map')}
            </button>
          </div>
        )}
      </div>
      <ErrorField
        margin="15px"
        divider={false}
        field={FORM_VALUES.coordinates}
        cypress="coordinates-field-error"
      />
      <div className="filters__options-wrapper">
        {!!locations?.length &&
          locations?.map(location => (
            <Chips
              dirty={true}
              type="button"
              iconSize={21}
              key={location.id}
              text={location.name}
              isTextPadding={false}
              cypress={`landmarks-${location.id}`}
              resetHandler={() => handleLocationDelete(location.id)}
              icon={location.locationGroupId === '1' ? MetroIcon : null}
            />
          ))}
      </div>
    </>
  );
};

SelectLocationMapComponent.propTypes = {
  initialLocation: PropTypes.shape({
    lng: PropTypes.number,
    lat: PropTypes.number
  })
};

export default SelectLocationMapComponent;
